import { useState, useEffect, useCallback, useRef } from 'react';
import request from '../../utils/request';
import './OrganizationMembersModal.css';

// Check if element is inside modal content
function isInsideModalContent(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return target.closest('.modal-content') !== null;
}

interface OrganizationMember {
  id: string;
  name: string;
  email: string;
  role: string;
  created_at: string;
}

interface OrganizationMembersModalProps {
  isOpen: boolean;
  onClose: () => void;
  organization: {
    id: string;
    name: string;
  } | null;
}

export default function OrganizationMembersModal({
  isOpen,
  onClose,
  organization,
}: OrganizationMembersModalProps) {
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
  });
  const overlayRef = useRef<HTMLDivElement>(null);
  const mouseDownInsideContent = useRef(false);

  // Fetch organization member list
  const fetchMembers = useCallback(async () => {
    if (!organization) return;
    setLoading(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response: any = await request.get(`/gradmin/organizations/${organization.id}/members`, {
        params: {
          page: pagination.page,
          page_size: pagination.pageSize,
        },
      });
      if (response.code === 0) {
        setMembers(response.data.items);
        setPagination(prev => ({
          ...prev,
          total: response.data.total,
        }));
      }
    } catch (error) {
      console.error('Failed to fetch organization members:', error);
    } finally {
      setLoading(false);
    }
  }, [organization, pagination.page, pagination.pageSize]);

  // Fetch data when modal opens
  useEffect(() => {
    if (isOpen && organization) {
      fetchMembers();
    }
  }, [isOpen, organization, fetchMembers]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setMembers([]);
      setPagination({ page: 1, pageSize: 10, total: 0 });
    }
  }, [isOpen]);

  // Pagination handler
  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const totalPages = Math.ceil(pagination.total / pagination.pageSize);

  // Get role display text
  const getRoleText = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Admin';
      case 'member':
        return 'Member';
      default:
        return role;
    }
  };

  if (!isOpen || !organization) return null;

  return (
    <div
      ref={overlayRef}
      className="modal-overlay"
      onMouseDown={e => {
        mouseDownInsideContent.current = isInsideModalContent(e.target);
      }}
      onClick={e => {
        if (e.target === overlayRef.current && !mouseDownInsideContent.current) {
          onClose();
        }
      }}
    >
      <div className="modal-content org-members-modal">
        <div className="modal-header">
          <h2 className="modal-title">Organization Members - {organization.name}</h2>
          <button className="modal-close" onClick={onClose}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              Loading...
            </div>
          ) : members.length === 0 ? (
            <div className="empty-state">No members</div>
          ) : (
            <>
              <table className="members-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Joined At</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map(member => (
                    <tr key={member.id}>
                      <td>{member.name}</td>
                      <td>{member.email}</td>
                      <td>
                        <span className={`role-badge role-${member.role}`}>
                          {getRoleText(member.role)}
                        </span>
                      </td>
                      <td>{member.created_at}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {pagination.total > pagination.pageSize && (
                <div className="pagination">
                  <button
                    className="pagination-btn"
                    disabled={pagination.page === 1}
                    onClick={() => handlePageChange(pagination.page - 1)}
                  >
                    Previous
                  </button>
                  <span className="pagination-current">
                    {pagination.page} / {totalPages}
                  </span>
                  <button
                    className="pagination-btn"
                    disabled={pagination.page >= totalPages}
                    onClick={() => handlePageChange(pagination.page + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
